import { ConfirmCustomizationOptions } from '../actions/confirm';
import { Button, ButtonType } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import strings from 'owa-strings/owa-common-strings';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

let styles = require('./ConfirmDialog.scss');

interface ConfirmDialogProps {
    title?: string;
    subText?: string;
    customizationOptions?: ConfirmCustomizationOptions;
    onOkCallback?: () => void;
    onCancelCallback?: () => void;
    onDismissCallback?: () => void;
    onContinueCallback?: () => void;
    onClose: () => void;
}

interface ConfirmDialogState {
    isShown: boolean;
}

let counter = 0;

class ConfirmDialog extends React.Component<ConfirmDialogProps, ConfirmDialogState> {
    public static defaultProps = {
        customizationOptions: {},
    };

    private delayedCallback: () => void;
    private cancelId = `cancel-${counter++}`;
    private okId = `ok-${counter++}`;
    private continueId = `continue-${counter++}`;

    constructor(props: any) {
        super(props);
        this.state = {
            isShown: true,
        };
    }

    render() {
        let customizationOptions = this.props.customizationOptions;
        let onOk = () => this.onClick(this.props.onOkCallback);
        let onCancel = () => this.onClick(this.props.onCancelCallback);
        let onContinue = () => this.onClick(this.props.onContinueCallback);

        return (
            <Dialog
                isOpen={this.state.isShown}
                type={DialogType.normal}
                onDismiss={this.onDismiss}
                onDismissed={this.onDismissed}
                subText={this.props.subText}
                title={this.props.title}
                containerClassName={customizationOptions.containerClassName}
                contentClassName={styles.content}
                modalProps={{
                    firstFocusableSelector: this.firstFocusableSelector,
                }}>
                {/** We have the form so that 'enter' in input fields will trigger the 'ok' confirm action */}

                <form
                    onSubmit={evt => {
                        evt.preventDefault();
                        onOk();
                    }}>
                    {customizationOptions.bodyElement}
                    <input className={styles.hideSubmitButton} type="submit" />
                </form>

                <DialogFooter>
                    <Button
                        id={this.okId}
                        buttonType={ButtonType.primary}
                        onClick={onOk}
                        title={customizationOptions.okToolTip}>
                        {customizationOptions.okText || strings.okButton}
                    </Button>
                    {customizationOptions.showContinueButton ? (
                        <Button
                            id={this.continueId}
                            buttonType={ButtonType.normal}
                            onClick={onContinue}
                            title={customizationOptions.continueToolTip}>
                            {customizationOptions.continueText || strings.continueButton}
                        </Button>
                    ) : null}
                    {customizationOptions.hideCancelButton ? null : (
                        <Button
                            id={this.cancelId}
                            buttonType={ButtonType.normal}
                            onClick={onCancel}
                            title={customizationOptions.cancelToolTip}>
                            {customizationOptions.cancelText || strings.cancelButton}
                        </Button>
                    )}
                </DialogFooter>
            </Dialog>
        );
    }

    private get firstFocusableSelector(): string {
        const { hideCancelButton, focusOnCancel, focusSelector } = this.props.customizationOptions;
        let selector: string;
        if (!hideCancelButton && focusOnCancel) {
            selector = `#${this.cancelId}`;
        } else if (focusSelector) {
            selector = focusSelector;
        } else {
            selector = `#${this.okId}`;
        }

        // The FocusTrapZone, inexplicably, prepends any `firstFocusableSelector` provided with a '.',
        // so trying to provide a straight id selector ends up throwing an exception.
        // To work around the issue, I'm prepending the selector with a class applied
        // to the content of the Dialog to "eat" the prepended '.'
        return `ms-Dialog-inner ${selector}`;
    }

    private onClick = callback => {
        if (callback) {
            if (
                this.props.customizationOptions &&
                this.props.customizationOptions.delayCallbackAfterAnimation
            ) {
                this.delayedCallback = callback;
            } else {
                callback();
            }
        }

        this.setState({
            isShown: false,
        });
    };

    private onDismiss = () => {
        if (this.props.onDismissCallback) {
            // We dismissed without issuing a callback.
            // Call 'cancel'
            this.props.onDismissCallback();
        }

        if (!this.props.customizationOptions.preventSoftDismiss) {
            this.setState({
                isShown: false,
            });
        }
    };

    private onDismissed = () => {
        if (this.delayedCallback) {
            this.delayedCallback();
            this.delayedCallback = null;
        }

        this.props.onClose();
    };
}

let instance = 0;

function showConfirmDialog(
    title: string,
    subText?: string,
    customizationOptions?: ConfirmCustomizationOptions,
    onOkCallback?: () => void,
    onCancelCallback?: () => void,
    onDismissCallback?: () => void,
    onContinueCallback?: () => void
) {
    // Initialize it and append it to the body.
    let confirmDialogDiv = document.createElement('div');
    confirmDialogDiv.id = `confirmDialog${instance++}`;
    document.body.appendChild(confirmDialogDiv);
    ReactDOM.render(
        <ConfirmDialog
            title={title}
            subText={subText}
            customizationOptions={customizationOptions}
            onOkCallback={onOkCallback}
            onCancelCallback={onCancelCallback}
            onDismissCallback={onDismissCallback}
            onContinueCallback={onContinueCallback}
            onClose={() => {
                ReactDOM.unmountComponentAtNode(confirmDialogDiv);
                document.body.removeChild(confirmDialogDiv);
            }}
        />,
        confirmDialogDiv
    );
}

export default showConfirmDialog;
